import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { BRAND } from '@/brand';
import { PublicFooter, PublicHeader } from '@/components/common/PublicChrome';
import {
  LEGAL_DOCS,
  legalLanguage,
  legalTexts,
  type LegalBlock,
  type LegalDocKey,
  type LegalTexts,
} from '@/features/legal/documents';
import { cn } from '@/lib/cn';
import { ROUTES } from '@/router';
import { useLocalizedPath } from '@/seo/useLocalePath';
import { usePageHead } from '@/seo/usePageHead';

/** `{email}`, a published document (`{risk}` / `{risk:’ni}`), `[[placeholder]]`. */
const TOKEN = new RegExp(
  String.raw`\{email\}|\{(${LEGAL_DOCS.join('|')})(?::([^}]*))?\}|\[\[([^\]]+)\]\]`,
  'g',
);

/**
 * One paragraph or list item: its text with the tokens turned into links and placeholders.
 * `localized` keeps a link in the address's language (`useLocalizedPath`).
 */
function richText(
  text: string,
  texts: LegalTexts,
  localized: (path: string) => string,
): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(TOKEN)) {
    const at = match.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const [token, doc, suffix, placeholder] = match;
    if (token === '{email}') {
      out.push(
        <a key={at} href={`mailto:${BRAND.contact}`} className="text-brand hover:underline">
          {BRAND.contact}
        </a>,
      );
    } else if (doc) {
      const key = doc as LegalDocKey;
      out.push(
        <Link key={at} to={localized(ROUTES[key])} className="text-brand hover:underline">
          {texts.documents[key].title}
          {suffix ?? ''}
        </Link>,
      );
    } else {
      // Not filled in yet: drawn so nobody reads it as the final text.
      out.push(
        <mark
          key={at}
          data-placeholder=""
          className="rounded-sm bg-pending-tint px-1 font-medium text-ink"
        >
          [{placeholder}]
        </mark>,
      );
    }
    last = at + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Block({ block, texts }: { block: LegalBlock; texts: LegalTexts }): JSX.Element {
  const localized = useLocalizedPath();
  if (typeof block === 'string') return <p>{richText(block, texts, localized)}</p>;
  return (
    <ul className="list-disc space-y-1.5 pl-5">
      {block.map((item, index) => (
        <li key={index}>{richText(item, texts, localized)}</li>
      ))}
    </ul>
  );
}

/**
 * docs/LEGAL.md — a legal text, open to everyone (only `/risk` is published so far).
 * Turkish for a Turkish reader, English for everyone else, with a note for a reader of another
 * language. Prerendered in English at `/risk` and Turkish at `/tr/risk` (scripts/prerender.mjs),
 * so it reads without JavaScript.
 */
export default function LegalPage({ doc }: { doc: LegalDocKey }): JSX.Element {
  const { t, i18n } = useTranslation();
  usePageHead(doc);
  const localized = useLocalizedPath();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const language = legalLanguage(locale);
  const texts = legalTexts(language);
  const legal = texts.documents[doc];
  // Neither of the two languages the texts are written in: say which one this is.
  const translated = !/^(en|tr)\b/i.test(locale ?? '');

  return (
    <div className="min-h-screen bg-canvas text-ink" data-testid="legal-page">
      <PublicHeader />

      <main className="mx-auto max-w-3xl space-y-8 px-4 py-8 sm:px-6" lang={language}>
        {/* Between the documents; hidden while only one is published. */}
        {LEGAL_DOCS.length > 1 && (
          <nav aria-label={t('legal.navLabel')} className="flex flex-wrap gap-2">
            {LEGAL_DOCS.map((key) => (
              <Link
                key={key}
                to={localized(ROUTES[key])}
                aria-current={key === doc ? 'page' : undefined}
                className={cn(
                  'rounded-sm border px-3 py-1.5 text-body transition-colors focus-visible:outline-none focus-visible:shadow-focus',
                  key === doc
                    ? 'border-brand bg-brand-tint text-ink'
                    : 'border-border-subtle text-ink-secondary hover:text-ink',
                )}
              >
                {t(`legal.${key}`)}
              </Link>
            ))}
          </nav>
        )}

        <header className="space-y-3">
          <h1 className="text-display-1 text-ink">{legal.title}</h1>
          <p className="text-caption text-ink-tertiary">
            {t('legal.updated', { date: texts.updated })}
          </p>
          {translated && (
            <p className="rounded-sm border border-border-subtle bg-surface-2 px-3 py-2 text-caption text-ink-secondary">
              {t('legal.otherLanguage')}
            </p>
          )}
          <p className="text-body-md text-ink-secondary">{legal.summary}</p>
        </header>

        <nav
          aria-label={t('legal.contents')}
          className="rounded-lg border border-border-subtle bg-surface-1 p-4"
        >
          <h2 className="mb-2 text-label text-ink-tertiary">{t('legal.contents')}</h2>
          <ol className="text-body sm:columns-2 sm:gap-x-6">
            {legal.sections.map((section) => (
              <li key={section.id} className="mb-1 break-inside-avoid">
                <a
                  href={`#${section.id}`}
                  className="text-ink-secondary hover:text-ink hover:underline"
                >
                  {section.heading}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {legal.sections.map((section) => (
          <section key={section.id} id={section.id} className="scroll-mt-6 space-y-3">
            <h2 className="text-title text-ink">{section.heading}</h2>
            <div className="space-y-3 text-body-md leading-relaxed text-ink-secondary">
              {section.blocks.map((block, index) => (
                <Fragment key={index}>
                  <Block block={block} texts={texts} />
                </Fragment>
              ))}
            </div>
          </section>
        ))}
      </main>

      <PublicFooter />
    </div>
  );
}
